import { io, Socket } from 'socket.io-client';

const API_SERVER_URL = process.env.REACT_APP_API_SOCKET_SERVER;

export enum WebSocketEventType {
   REFRESH_BALANCE = 'refresh_balance',
}

export interface WebSocketEvent {
   type: WebSocketEventType;
   data?: any;
}

export type WebSocketEventHandler = (event: WebSocketEvent) => void;

class WebSocketService {
   private socket: Socket | null = null;
   private eventHandlers: Map<WebSocketEventType, Set<WebSocketEventHandler>> = new Map();
   private isConnecting = false;
   private currentUserId: string | null = null;

   private getSocketUrl(): string {
      if (!API_SERVER_URL) {
         throw new Error('REACT_APP_API_SOCKET_SERVER is not defined');
      }

      // Socket.IO automatically handles protocol conversion
      return API_SERVER_URL;
   }

   private connect(): void {
      if (this.socket?.connected || this.isConnecting) {
         return;
      }

      this.isConnecting = true;

      try {
         const socketUrl = this.getSocketUrl();
         this.socket = io(socketUrl, {
            withCredentials: true,
            transports: ['websocket', 'polling'], // Fallback to polling if WebSocket fails
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            reconnectionAttempts: 5,
         });

         this.socket.on('connect', () => {
            // console.log('Socket.IO connected');
            this.isConnecting = false;
            // Join user room if userId is available
            if (this.currentUserId) {
               this.joinUserRoom(this.currentUserId);
            }
         });

         this.socket.on('disconnect', (reason) => {
            // console.log('Socket.IO disconnected:', reason);
            this.isConnecting = false;
            // Leave user room on disconnect
            if (this.currentUserId) {
               this.leaveUserRoom(this.currentUserId);
            }
         });

         this.socket.on('connect_error', (error) => {
            // console.error('Socket.IO connection error:', error);
            this.isConnecting = false;
         });

         // Listen for all registered event types
         this.eventHandlers.forEach((handlers, eventType) => {
            if (handlers.size > 0) {
               this.setupEventListeners(eventType);
            }
         });
      } catch (error) {
         console.error('Failed to create Socket.IO connection:', error);
         this.isConnecting = false;
      }
   }

   private setupEventListeners(eventType: WebSocketEventType): void {
      if (!this.socket) return;

      // Remove existing listener if any
      this.socket.off(eventType);

      // Add new listener
      this.socket.on(eventType, (data?: any) => {
         const event: WebSocketEvent = {
            type: eventType,
            data,
         };
         console.log('WebSocketEvent:', event);
         this.handleEvent(event);
      });
   }

   private handleEvent(event: WebSocketEvent): void {
      const handlers = this.eventHandlers.get(event.type);
      if (handlers) {
         handlers.forEach((handler) => {
            try {
               handler(event);
            } catch (error) {
               console.error(`Error in Socket.IO event handler for ${event.type}:`, error);
            }
         });
      }
   }

   public subscribe(eventType: WebSocketEventType, handler: WebSocketEventHandler): () => void {
      if (!this.eventHandlers.has(eventType)) {
         this.eventHandlers.set(eventType, new Set());
      }

      this.eventHandlers.get(eventType)!.add(handler);

      // Connect if not already connected
      if (!this.socket || !this.socket.connected) {
         this.connect();
      } else {
         // If already connected, set up listener for this event type
         this.setupEventListeners(eventType);
      }

      // Return unsubscribe function
      return () => {
         const handlers = this.eventHandlers.get(eventType);
         if (handlers) {
            handlers.delete(handler);
            if (handlers.size === 0) {
               this.eventHandlers.delete(eventType);
               // Remove listener from socket if no more handlers
               if (this.socket) {
                  this.socket.off(eventType);
               }
            }
         }
      };
   }

   public disconnect(): void {
      // Leave user room before disconnecting
      if (this.currentUserId && this.socket?.connected) {
         this.leaveUserRoom(this.currentUserId);
      }

      if (this.socket) {
         this.socket.disconnect();
         this.socket = null;
      }
      this.eventHandlers.clear();
      this.isConnecting = false;
      this.currentUserId = null;
   }

   public isConnected(): boolean {
      return this.socket?.connected ?? false;
   }

   /**
    * Join a user room to receive user-specific events
    * @param userId The user ID to join the room for
    */
   private joinUserRoom(userId: string): void {
      if (!this.socket) {
         // console.warn('Socket not initialized, cannot join user room');
         return;
      }

      this.socket.emit('join_user_room', { userId });
      // console.log(`Joined user room for userId: ${userId}`);
   }

   /**
    * Leave a user room
    * @param userId The user ID to leave the room for
    */
   private leaveUserRoom(userId: string): void {
      if (!this.socket) {
         return;
      }

      this.socket.emit('leave_user_room', { userId });
      // console.log(`Left user room for userId: ${userId}`);
   }

   /**
    * Set the current user ID and join their room if connected
    * @param userId The user ID
    */
   public setUserId(userId: string | null): void {
      if (userId === this.currentUserId) {
         return; // Already set to this user
      }

      // Leave previous user room if any
      if (this.currentUserId && this.socket?.connected) {
         this.leaveUserRoom(this.currentUserId);
      }

      this.currentUserId = userId;

      // Join new user room if connected
      if (userId && this.socket?.connected) {
         this.joinUserRoom(userId);
      }
   }
}

// Export singleton instance
export const webSocketService = new WebSocketService();
